<template>
  <v-dialog
    fullscreen
    persistent
    value="true"
    transition="dialog-bottom-transition"
    data-test-id="module-builder"
  >
    <v-card
      class="module-builder-card d-flex flex-column"
      :class="{ 'full-screen-builder': !compact }"
    >
      <DrawerToolbar class="flex-grow-0" :title="title" admin @close="$emit('close')" />

      <div v-if="showIntro" class="intro-content">
        <slot name="intro-content" />
      </div>

      <v-container v-else-if="compact" class="px-50">
        <v-row>
          <v-col v-if="!completed" cols="12" class="d-flex justify-space-between">
            <Button
              :class="loading || stepIndex === 0 ? 'hidden' : ''"
              iconLeft="mdi-chevron-left"
              outlined
              color="brandPrimaryDark"
              :disabled="continueLoading"
              @click="handlePrevious"
              >Back</Button
            >
            <Button
              :iconRight="isLastStep ? null : 'mdi-chevron-right'"
              @click="handleNext"
              :loading="continueLoading"
              :disabled="loading"
            >
              {{ continueLabel }}
            </Button>
          </v-col>
          <v-col v-if="!completed" cols="5">
            <BuilderSteps
              :steps="steps"
              :current-step-index="stepIndex"
              :current-substep-index="substepIndex"
              :current-step-key="currentStepKey"
              :current-substep-key="sidebarSubstepKey"
              :highest-step-reached="stepIndex"
              :highest-substep-reached="substepIndex"
              :free-navigation="freeNavigation"
              :loading="loading || continueLoading"
              @navigate="goToStep"
            />
          </v-col>
          <v-col :cols="completed ? 12 : 7">
            <v-card class="text-left pa-4" :elevation="completed ? 0 : 2">
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="brandPrimary"
                width="2"
                size="25"
              />
              <slot v-else-if="completed" name="success-content" />
              <slot
                v-else
                name="step-content"
                :current-step-key="currentStepKey"
                :current-substep-key="currentSubstepKey"
              />
            </v-card>
          </v-col>
          <v-col v-if="!completed" cols="12" class="d-flex justify-end">
            <Button
              :iconRight="isLastStep ? null : 'mdi-chevron-right'"
              @click="handleNext"
              :loading="continueLoading"
              :disabled="loading"
              >{{ continueLabel }}</Button
            >
          </v-col>
        </v-row>
      </v-container>

      <div v-else class="d-flex align-stretch flex-grow-1">
        <aside v-if="!completed" class="builder-sidebar pa-4">
          <BuilderSteps
            :steps="steps"
            :current-step-index="stepIndex"
            :current-substep-index="substepIndex"
            :current-step-key="currentStepKey"
            :current-substep-key="sidebarSubstepKey"
            :highest-step-reached="stepIndex"
            :highest-substep-reached="substepIndex"
            :compact="false"
            :free-navigation="freeNavigation"
            :loading="loading || continueLoading"
            @navigate="goToStep"
          />
        </aside>
        <v-container class="pa-6 builder-content" fluid>
          <v-row>
            <v-col
              v-if="!completed"
              :cols="contentCols"
              :offset="contentOffset"
              class="d-flex justify-space-between"
            >
              <Button
                :class="loading || stepIndex === 0 ? 'hidden' : ''"
                iconLeft="mdi-chevron-left"
                outlined
                color="brandPrimaryDark"
                :disabled="continueLoading"
                @click="handlePrevious"
                >Back</Button
              >
              <Button
                :iconRight="isLastStep ? null : 'mdi-chevron-right'"
                @click="handleNext"
                :loading="continueLoading"
                :disabled="loading"
              >
                {{ continueLabel }}
              </Button>
            </v-col>
            <v-col :cols="contentCols" :offset="contentOffset">
              <v-card class="text-left pa-6" :elevation="completed ? 0 : 2">
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  color="brandPrimary"
                  width="2"
                  size="25"
                />
                <slot v-else-if="completed" name="success-content" />
                <slot
                  v-else
                  name="step-content"
                  :current-step-key="currentStepKey"
                  :current-substep-key="currentSubstepKey"
                />
              </v-card>
            </v-col>
            <v-col
              v-if="!completed"
              :cols="contentCols"
              :offset="contentOffset"
              class="d-flex justify-end"
            >
              <Button
                :iconRight="isLastStep ? null : 'mdi-chevron-right'"
                @click="handleNext"
                :loading="continueLoading"
                :disabled="loading"
                >{{ continueLabel }}</Button
              >
            </v-col>
          </v-row>
        </v-container>
        <aside v-if="showPreviewColumn" class="builder-preview pa-6">
          <slot name="preview" :current-step-key="currentStepKey" />
        </aside>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import BuilderSteps from "./components/BuilderSteps.vue";
import Button from "@/components/generic/Button.vue";

export default {
  name: "ModuleBuilder",
  components: {
    DrawerToolbar,
    BuilderSteps,
    Button,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    steps: {
      type: Array,
      required: true,
    },
    onNext: {
      type: Function,
      default: () => {},
    },
    onBack: {
      type: Function,
      default: () => {},
    },
    onStepClick: {
      type: Function,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Override for the last-step button label (e.g. "Save and Publish" when publishing). Default: "Save" */
    lastStepLabel: {
      type: String,
      default: "Save",
    },
    compact: {
      type: Boolean,
      default: true,
    },
    // When true, render #intro-content under the toolbar and hide the step chrome.
    showIntro: {
      type: Boolean,
      default: false,
    },
    freeNavigation: {
      type: Boolean,
      default: false,
    },
    initialStepKey: {
      type: String,
      default: null,
    },
    // Escape hatch for parents that manage sidebar-highlight state
    // externally (e.g. nonLinearSubsteps steps whose highlight is a function
    // of a side-panel's open/close state rather than the wizard's linear
    // position). Contract:
    //   - `undefined` (default) → no override; sidebar mirrors the internal
    //     linear-flow position. This is the legacy path (SurveyBuilder).
    //   - `null`                → override *to nothing*; no substep highlighted.
    //   - `"<substepKey>"`      → override; that specific substep highlighted.
    // Only affects the sidebar; the `#step-content` slot's `currentSubstepKey`
    // is unchanged.
    activeSubstepKeyOverride: {
      type: String,
      default: undefined,
    },
  },
  emits: ["close", "update:currentStepKey", "substep-click", "builder-finished"],
  data() {
    return {
      stepIndex: 0,
      substepIndex: 0,
      continueLoading: false,
      completed: false,
      seededFromInitial: false,
    };
  },
  computed: {
    currentStepKey() {
      return this.steps[this.stepIndex]?.key ?? null;
    },
    currentStep() {
      return this.steps[this.stepIndex] ?? null;
    },
    currentSubstepKey() {
      const step = this.currentStep;
      if (!step?.substeps?.length) return null;
      const sub = step.substeps[this.substepIndex];
      return sub?.key ?? sub?.label ?? null;
    },
    // The substep key the sidebar should highlight on the current step.
    // Defers to `activeSubstepKeyOverride` when the parent supplies one,
    // otherwise mirrors the internal linear-flow position.
    sidebarSubstepKey() {
      return this.activeSubstepKeyOverride === undefined
        ? this.currentSubstepKey
        : this.activeSubstepKeyOverride;
    },
    // Live Preview column: full-screen only, when the current step sets `showPreview`.
    showPreviewColumn() {
      if (this.completed || this.compact) return false;
      return !!this.currentStep?.showPreview;
    },
    // Full-screen layout only: `fullWidth` and preview steps use all remaining columns so Continue stays aligned.
    contentCols() {
      if (this.completed) return 12;
      if (this.currentStep?.fullWidth || this.showPreviewColumn) return 12;
      return 8;
    },
    contentOffset() {
      if (this.completed || this.currentStep?.fullWidth || this.showPreviewColumn) return 0;
      return 2;
    },
    isLastStep() {
      const onLastStep = this.stepIndex === this.steps.length - 1;
      const step = this.currentStep;
      const onLastSubstep =
        !step?.substeps?.length ||
        step.nonLinearSubsteps ||
        this.substepIndex === step.substeps.length - 1;
      return onLastStep && onLastSubstep;
    },
    continueLabel() {
      return this.isLastStep ? this.lastStepLabel : "Continue";
    },
  },
  watch: {
    currentStepKey: {
      immediate: true,
      handler(key) {
        this.$emit("update:currentStepKey", key);
      },
    },
    initialStepKey: {
      immediate: true,
      handler(key) {
        if (this.seededFromInitial || !key) return;
        const idx = this.steps.findIndex((s) => s.key === key);
        if (idx >= 0) {
          this.stepIndex = idx;
          this.substepIndex = 0;
          this.seededFromInitial = true;
        }
      },
    },
  },
  methods: {
    async handleNext() {
      this.continueLoading = true;
      const result = await this.onNext?.(this.currentStepKey, this.currentSubstepKey);
      if (result === false) {
        this.continueLoading = false;
        return;
      }
      const step = this.currentStep;
      const hasLinearSubsteps = step?.substeps?.length && !step.nonLinearSubsteps;
      if (hasLinearSubsteps && this.substepIndex < step.substeps.length - 1) {
        this.substepIndex += 1;
      } else if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex += 1;
        this.substepIndex = 0;
      } else {
        this.completed = true;
        this.$emit("builder-finished");
      }
      this.continueLoading = false;
    },
    async goToStep({ stepIndex, substepIndex, source }) {
      if (this.continueLoading) return;

      // Give the parent a chance to validate the CURRENT step before we
      // navigate away — returning false blocks the click.
      const clickResult = await this.onStepClick?.(this.currentStepKey, this.currentSubstepKey);
      if (clickResult === false) return;

      const setIndeces = (nextStepIndex, nextSubstepIndex) => {
        this.stepIndex = nextStepIndex;
        this.substepIndex = nextSubstepIndex;
      };

      // Fires whenever a substep-row click resolves (even if indices didn't
      // actually change, so parents can re-open a sheet on re-click).
      const emitSubstepClick = () => {
        if (source !== "substep") return;
        const targetStep = this.steps[stepIndex];
        const targetSubstep = targetStep?.substeps?.[substepIndex];
        if (!targetSubstep) return;
        this.$emit("substep-click", {
          stepKey: targetStep.key,
          substepKey: targetSubstep.key ?? targetSubstep.label ?? null,
        });
      };

      const isBackward =
        stepIndex < this.stepIndex ||
        (stepIndex === this.stepIndex && substepIndex <= this.substepIndex);
      if (isBackward) {
        setIndeces(stepIndex, substepIndex);
        emitSubstepClick();
        return;
      }

      if (!this.freeNavigation) return;

      const targetStep = this.steps[stepIndex];
      const targetSubsteps = targetStep?.substeps ?? [];

      // Parent-row click (with or without substeps): only require the target
      // STEP itself to be completed. Per-substep clickability is handled by
      // the substep-click branch below.
      if (source !== "substep") {
        if (targetStep?.completed) setIndeces(stepIndex, substepIndex);
        return;
      }

      // Substep click: can navigate to substep even if parent step is not completed
      const targetSubstep = targetSubsteps[substepIndex];
      if (!targetSubstep?.completed) return;

      setIndeces(stepIndex, substepIndex);
      emitSubstepClick();
    },
    async handlePrevious() {
      // Same pattern as handleNext — parent validates and returns false to
      // block the transition.
      const result = await this.onBack?.(this.currentStepKey, this.currentSubstepKey);
      if (result === false) return;

      const step = this.currentStep;
      const hasLinearSubsteps = step?.substeps?.length && !step.nonLinearSubsteps;
      if (hasLinearSubsteps && this.substepIndex > 0) {
        this.substepIndex -= 1;
      } else if (this.stepIndex > 0) {
        this.stepIndex -= 1;
        const prevStep = this.currentStep;
        const prevHasLinearSubsteps = prevStep?.substeps?.length && !prevStep.nonLinearSubsteps;
        this.substepIndex = prevHasLinearSubsteps ? prevStep.substeps.length - 1 : 0;
      }
    },
  },
};
</script>

<style scoped>
.module-builder-card {
  min-height: 100vh;
}
.builder-sidebar {
  width: 276px;
  flex-shrink: 0;
  background-color: white;
  border-right: 1px solid var(--v-grey-base);
}
.builder-content {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
}
.builder-preview {
  width: 475px;
  flex-shrink: 0;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  border-left: 1px solid var(--v-grey-base);
}
.full-screen-builder {
  background-color: var(--v-lightGrey-base);
}
.intro-content {
  flex: 1 1 auto;
  min-height: 0;
  background-color: white;
  overflow-y: auto;
}
</style>
